import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const empty = { code: "", discount_percent: 0, discount_flat: 0, min_order: 0, is_active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", {
        ...form,
        discount_percent: Number(form.discount_percent) || 0,
        discount_flat: Number(form.discount_flat) || 0,
        min_order: Number(form.min_order) || 0,
      });
      toast.success("Coupon created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success("Coupon deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-coupons-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-amber-400">Promotions</div>
          <h1 className="font-serif-display text-3xl mt-2">Coupons</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="new-coupon-btn">
              <Plus className="h-4 w-4 mr-2" /> New Coupon
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="coupon-dialog">
            <DialogHeader><DialogTitle className="font-serif-display text-2xl">New coupon</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Code</Label>
                <Input required value={form.code} placeholder="ATSHREE10"
                  onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                  data-testid="coupon-code" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>% off</Label>
                  <Input type="number" min="0" max="100" value={form.discount_percent}
                    onChange={(e) => setForm({...form, discount_percent: e.target.value})}
                    data-testid="coupon-percent" />
                </div>
                <div><Label>Flat ₹ off</Label>
                  <Input type="number" min="0" value={form.discount_flat}
                    onChange={(e) => setForm({...form, discount_flat: e.target.value})}
                    data-testid="coupon-flat" />
                </div>
              </div>
              <div><Label>Min order ₹</Label>
                <Input type="number" min="0" value={form.min_order}
                  onChange={(e) => setForm({...form, min_order: e.target.value})}
                  data-testid="coupon-min-order" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active}
                  onCheckedChange={(v) => setForm({...form, is_active: v})}
                  data-testid="coupon-active" />
                <Label>Active</Label>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-amber-500 text-slate-900 hover:bg-amber-400"
                  data-testid="coupon-save">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.length === 0 ? (
          <div className="col-span-full text-center text-slate-500 py-12" data-testid="coupons-empty">
            No coupons yet.
          </div>
        ) : coupons.map((c, i) => (
          <div key={c.id} className="bg-slate-800/60 border border-white/5 rounded-lg p-5"
               data-testid={`coupon-${i}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-accent text-lg tracking-wider">{c.code}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {c.discount_percent > 0 && `${c.discount_percent}% off`}
                  {c.discount_flat > 0 && `₹${c.discount_flat} off`}
                  {c.min_order > 0 && ` · min ₹${c.min_order}`}
                </div>
              </div>
              <Badge className={c.is_active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-700"}>
                {c.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(c.id)}
              className="mt-4 text-red-400 hover:bg-red-500/10" data-testid={`delete-coupon-${i}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
